import React, { useState, useEffect } from 'react';
import { Building2, MapPin, TrendingUp, AlertOctagon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { venueAPI } from '../lib/api';

const EnterpriseMappingPage = () => {
  const [venues, setVenues] = useState([]);

  useEffect(() => {
    // Fetch real venues from your API to make the list authentic
    const fetchVenues = async () => {
      try {
        const response = await venueAPI.list();
        // Add mock compliance scores for UI demonstration
        const enhancedVenues = response.data.map(v => ({
          ...v,
          ehoScore: Math.floor(Math.random() * (100 - 65 + 1)) + 65, // Random score 65-100
          pendingTasks: Math.floor(Math.random() * 12),
        }));
        setVenues(enhancedVenues);
      } catch (error) {
        console.error("Failed to load venues", error);
      }
    };
    fetchVenues();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#FF6600] uppercase tracking-tight">
            Enterprise Mapping
          </h1>
          <p className="text-[#A1A1AA] mt-1">
            Group-wide compliance visibility and regional performance.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-[#1E1E1E] border-[#3F3F46]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[#A1A1AA] text-sm font-bold uppercase flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#FF6600]" />
              Total Venues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{venues.length}</div>
          </CardContent>
        </Card>
        <Card className="bg-[#1E1E1E] border-[#3F3F46]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[#A1A1AA] text-sm font-bold uppercase flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#00E676]" />
              Group Avg. Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#00E676]">88%</div>
          </CardContent>
        </Card>
        <Card className="bg-[#1E1E1E] border-[#3F3F46]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[#A1A1AA] text-sm font-bold uppercase flex items-center gap-2">
              <AlertOctagon className="w-4 h-4 text-[#FF3B30]" />
              Critical Risks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#FF3B30]">2</div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#1E1E1E] border-[#3F3F46]">
        <CardHeader>
          <CardTitle className="text-white">Venue Performance Matrix</CardTitle>
          <CardDescription className="text-[#A1A1AA]">Real-time compliance status across all mapped locations.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader className="bg-[#0A0A0A]">
              <TableRow className="border-[#3F3F46] hover:bg-transparent">
                <TableHead className="text-[#A1A1AA] font-bold uppercase text-xs">Venue</TableHead>
                <TableHead className="text-[#A1A1AA] font-bold uppercase text-xs">Location</TableHead>
                <TableHead className="text-[#A1A1AA] font-bold uppercase text-xs text-center">EHO Score</TableHead>
                <TableHead className="text-[#A1A1AA] font-bold uppercase text-xs text-right">Pending Tasks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {venues.length === 0 ? (
                <TableRow className="border-[#3F3F46]">
                  <TableCell colSpan={4} className="text-center text-[#A1A1AA] py-6">Loading enterprise data...</TableCell>
                </TableRow>
              ) : (
                venues.sort((a,b) => a.ehoScore - b.ehoScore).map((venue) => (
                  <TableRow key={venue.id} className="border-[#3F3F46] hover:bg-[#27272A]/50">
                    <TableCell className="font-medium text-white">{venue.name}</TableCell>
                    <TableCell className="text-[#A1A1AA] flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {venue.address || 'Unmapped'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={
                        venue.ehoScore >= 90 ? 'bg-[#00E676]/20 text-[#00E676]' :
                        venue.ehoScore >= 75 ? 'bg-[#FFB300]/20 text-[#FFB300]' :
                        'bg-[#FF3B30]/20 text-[#FF3B30]'
                      }>
                        {venue.ehoScore}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-white font-medium">{venue.pendingTasks}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnterpriseMappingPage;